import {useState} from "react";
import classNames from "classnames";
import {Product} from "@/entities/product";
import {useMainCollections} from "../model/hooks/useMainCollections";
import {CatalogCard} from "@/widgets/catalog-card";
import {Svg} from "@/shared/ui/Svg";
import {Link} from "@/shared/ui/Link";
import {Picture} from "@/shared/ui/Picture";
import {Button} from "@/shared/ui/Button";
import useViewItemBatcher from "@/Hooks/useViewItemBatcher";
import styles from "./MainCollections.module.scss";

type Set = {
    title: string;
    staticImagePath: string;
    dataCardSet: Product[];
    link: string;
    linkName: string;
}

type Props = {
    titleSection: string;
    allCollectionsLink: string;
    allCollectionsLinkName: string;
    sets: Set[];
}

export const MainCollections = ({titleSection, allCollectionsLink, allCollectionsLinkName, sets}: Props) => {
    const [currentIndex, setCurrentIndex] = useState(0);

    const currentSet = sets[currentIndex];

    const {
        carouselRef,
        onPrevButtonClick,
        onNextButtonClick,
        prevBtnDisabled,
        nextBtnDisabled
    } = useMainCollections();

    const {registerView} = useViewItemBatcher();

    if (!currentSet) {
        return null;
    }

    return (
        <div className={styles.container}>
            <div className={`globalContainer`}><p className={styles.titleSection}>{titleSection}</p></div>
            <div className={styles.contentContainer}>
                <div className={`globalContainer`}>
                    <div className={styles.titleContainer}>
                        {sets.map((set, index) => {
                            return (
                                <p
                                    key={set.title}
                                    onClick={() => setCurrentIndex(index)}
                                    className={classNames(styles.titleCollection, {
                                        [styles.active]: index === currentIndex,
                                    })}
                                >
                                    {set.title}
                                </p>
                            );
                        })}
                        <Link
                            href={allCollectionsLink}
                            className={classNames(styles.titleCollection, styles.allCollectionsLink)}
                        >
                            {allCollectionsLinkName}
                        </Link>
                    </div>
                </div>
                <div className={styles.carousel}>
                    <div className={`globalContainer`}>
                        <div className={styles.carouselContent}>
                            <Link href={currentSet.link} className={styles.staticImagePath}>
                                <Picture src={currentSet.staticImagePath} alt={currentSet.title} />
                            </Link>
                            <div ref={carouselRef} className={styles.carouselViewport}>
                                <div className={styles.carouselContainer}>
                                    {currentSet.dataCardSet.map((product, i) => {
                                        return (
                                            <CatalogCard
                                                key={product.id}
                                                product={product}
                                                className={styles.carouselSlide}
                                                index={i + 1}
                                                registerView={registerView}
                                            />
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                    {currentSet.dataCardSet.length > 1 && (
                        <>
                            <button
                                type={`button`}
                                className={classNames(styles.button, styles.prev, {[styles.hidden]: prevBtnDisabled})}
                                onClick={onPrevButtonClick}
                            >
                                <Svg id={`arrowLeft`} className={styles.arrowIcon} />
                            </button>
                            <button
                                type={`button`}
                                className={classNames(styles.button, styles.next, {[styles.hidden]: nextBtnDisabled})}
                                onClick={onNextButtonClick}
                            >
                                <Svg id={`arrowRight`} className={styles.arrowIcon} />
                            </button>
                        </>
                    )}
                </div>
                <div className={`globalContainer`}>
                    <Link href={currentSet.link}>
                        <Button variant={`tertiary`} fullWidth>{currentSet.linkName}</Button>
                    </Link>
                </div>
            </div>
        </div>
    );
}
